import {
  computed,
  nextTick,
  onBeforeUnmount,
  ref,
  useId,
  watch,
} from 'vue'
import {
  useFloating,
  offset as offsetFunc,
  shift,
  size,
  autoUpdate,
} from '@floating-ui/vue'

import type { UseAppSelectCoreOptions, UseAppSelectCoreReturn } from '../types/core'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { APP_FORM_STATE } from '@shared/ui/fields'

const PANEL_MAX_HEIGHT = 320

/**
 * Headless behavior core for every select skin: open/close, keyboard nav,
 * click-outside, highlight tracking, floating position. Returns ONLY behavior
 * and semantic state — never design-system classes, colors or icons. A skin
 * maps the semantic state to its own visuals (see `useAppSelectClasses` for the
 * default `app-select__*` skin).
 *
 * Markup contract a skin's SFC MUST honor:
 *  - Bind `triggerRef` to the trigger element, `inputRef` to the `<input>`,
 *    `panelRef` to the (teleported) panel.
 *  - Give each option element `data-app-select-option="<index>"` (0-based,
 *    matching `displayedOptions` order) — used to scroll the highlighted row
 *    into view.
 *  - Roles: trigger `role="combobox"`, panel `role="listbox"`, option `role="option"`.
 *  - Wire handlers: input `@focus`→`handleInputFocus`, `@keydown`→`handleTriggerKeydown`;
 *    option `@click`→`handleSelect`; chevron→`handleChevronClick`;
 *    clear-value→`handleClearValueClick`.
 */
export const useAppSelectCore = <TValue = string | number>(
  options: UseAppSelectCoreOptions<TValue>,
): UseAppSelectCoreReturn<TValue> => {
  const {
    props,
    displayedOptions,
    mode,
    selection,
    onOpen,
    onClose,
  } = options

  // Template refs (bound from SFC via `:ref="..."`).
  const triggerRef = ref<HTMLElement | null>(null)
  const inputRef = ref<HTMLInputElement | null>(null)
  const panelRef = ref<HTMLElement | null>(null)

  const isOpen = ref(false)
  const highlightedIndex = ref(-1)
  const panelId = useId()
  const labelId = useId()

  // ── Derived state ──────────────────────────────────────────────

  const isDisabled = computed<boolean>(() => props.disabled === true)
  const isError = computed<boolean>(() => props.state === APP_FORM_STATE.Error)

  const isSuccess = computed<boolean>(() => props.state === APP_FORM_STATE.Success)

  // Raw form state, exposed so a skin can derive its own visuals.
  const refState = computed(() => props.state)

  // Semantic highlight predicate — lets a skin paint its active row without
  // referencing `highlightedIndex` directly.
  const isHighlighted = (index: number): boolean => index === highlightedIndex.value

  // ── Index helpers ─────────────────────────────────────────────
  // All helpers iterate `displayedOptions` so keyboard navigation,
  // highlight, and scroll-into-view stay aligned with what's actually rendered.

  const findFirstNonDisabledIndex = (): number => {
    const opts = displayedOptions.value
    for (let i = 0; i < opts.length; i += 1) {
      if (!opts[i]?.disabled) return i
    }
    return -1
  }

  const findLastNonDisabledIndex = (): number => {
    const opts = displayedOptions.value
    for (let i = opts.length - 1; i >= 0; i -= 1) {
      if (!opts[i]?.disabled) return i
    }
    return -1
  }

  const findNextNonDisabledIndex = (from: number): number => {
    const opts = displayedOptions.value
    const len = opts.length
    if (len === 0) return -1
    for (let step = 1; step <= len; step += 1) {
      const next = (from + step) % len
      if (!opts[next]?.disabled) return next
    }
    return -1
  }

  const findPrevNonDisabledIndex = (from: number): number => {
    const opts = displayedOptions.value
    const len = opts.length
    if (len === 0) return -1
    for (let step = 1; step <= len; step += 1) {
      const prev = (from - step + len) % len
      if (!opts[prev]?.disabled) return prev
    }
    return -1
  }

  // `!isOpen` keeps this mutually exclusive with the searchable wrapper's
  // clear-search button (only shown while open, i.e. when query !== '').
  const showClearValueButton = computed<boolean>(() =>
    Boolean(props.clearable)
    && selection.hasValue.value
    && !isOpen.value,
  )

  // ── Open / close ──────────────────────────────────────────────

  const open = (initialHighlight?: number): void => {
    if (isDisabled.value) return
    isOpen.value = true
    if (initialHighlight !== undefined) {
      highlightedIndex.value = initialHighlight
    }
    else {
      const preferred = selection.initialHighlightIndex.value
      if (preferred >= 0) {
        highlightedIndex.value = preferred
      }
      else {
        highlightedIndex.value = selection.highlightFirstOnOpen === false
          ? -1
          : findFirstNonDisabledIndex()
      }
    }
    onOpen?.()
  }

  const close = (): void => {
    if (!isOpen.value) return
    isOpen.value = false
    highlightedIndex.value = -1
    onClose?.()
  }

  const toggle = (): void => {
    if (isOpen.value) {
      close()
      return
    }
    open()
  }

  // ── Selection ─────────────────────────────────────────────────

  const handleSelect = (option: AppSelectOption<TValue>): void => {
    if (option.disabled) return
    selection.toggleSelection(option)
    if (selection.shouldCloseAfterSelect) {
      close()
      void nextTick(() => inputRef.value?.blur())
    }
  }

  const handleClearValueClick = (): void => {
    if (isDisabled.value) return
    selection.clearValue()
  }

  // ── User interaction handlers ─────────────────────────────────

  const focusInput = (): void => {
    inputRef.value?.focus()
  }

  const handleChevronClick = (): void => {
    if (isDisabled.value) return
    toggle()
    void nextTick(() => inputRef.value?.focus())
  }

  const handleInputFocus = (): void => {
    if (isDisabled.value) return
    if (!isOpen.value) {
      open()
    }
  }

  // ── Keyboard handling ─────────────────────────────────────────

  const handleTriggerKeydown = (event: KeyboardEvent): void => {
    if (isDisabled.value) return
    const { key } = event

    if (!isOpen.value) {
      if (key === 'Enter' || (!mode.inputEditable && key === ' ')) {
        event.preventDefault()
        toggle()
        return
      }
      if (key === 'ArrowDown') {
        event.preventDefault()
        open(findFirstNonDisabledIndex())
        return
      }
      if (key === 'ArrowUp') {
        event.preventDefault()
        open(findLastNonDisabledIndex())
        return
      }
      return
    }

    // isOpen === true
    if (key === 'Escape') {
      event.preventDefault()
      close()
      return
    }
    if (key === 'Tab') {
      close()
      return
    }
    if (key === 'ArrowDown') {
      event.preventDefault()
      const next = findNextNonDisabledIndex(highlightedIndex.value)
      if (next !== -1) highlightedIndex.value = next
      return
    }
    if (key === 'ArrowUp') {
      event.preventDefault()
      const prev = findPrevNonDisabledIndex(highlightedIndex.value)
      if (prev !== -1) highlightedIndex.value = prev
      return
    }
    if (key === 'Home') {
      event.preventDefault()
      highlightedIndex.value = findFirstNonDisabledIndex()
      return
    }
    if (key === 'End') {
      event.preventDefault()
      highlightedIndex.value = findLastNonDisabledIndex()
      return
    }
    if (key === 'Enter') {
      event.preventDefault()
      const idx = highlightedIndex.value
      const option = displayedOptions.value[idx]
      if (idx >= 0 && option !== undefined && !option.disabled) {
        handleSelect(option)
      }
    }
  }

  // ── Click outside ─────────────────────────────────────────────

  const handleDocumentMousedown = (event: MouseEvent): void => {
    const target = event.target as Node | null
    if (!target) return
    if (triggerRef.value?.contains(target)) return
    if (panelRef.value?.contains(target)) return
    close()
  }

  watch(isOpen, (nowOpen) => {
    if (nowOpen) {
      document.addEventListener('mousedown', handleDocumentMousedown)
    }
    else {
      document.removeEventListener('mousedown', handleDocumentMousedown)
    }
  })

  // ── Cleanup ───────────────────────────────────────────────────

  onBeforeUnmount(() => {
    document.removeEventListener('mousedown', handleDocumentMousedown)
  })

  // ── Scroll highlighted into view ──────────────────────────────

  watch(highlightedIndex, (index) => {
    if (index < 0) return
    if (!panelRef.value) return
    const optionEl = panelRef.value.querySelector<HTMLElement>(
      `[data-app-select-option="${index}"]`,
    )
    optionEl?.scrollIntoView({ block: 'nearest' })
  })

  // ── Floating UI ───────────────────────────────────────────────

  const { floatingStyles } = useFloating(triggerRef, panelRef, {
    placement: 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offsetFunc(4),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply ({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            width: `${Math.max(rects.reference.width, props.panelMinWidth ?? 0)}px`,
            maxHeight: `${Math.min(availableHeight, PANEL_MAX_HEIGHT)}px`,
          })
        },
      }),
    ],
  })

  return {
    triggerRef,
    inputRef,
    panelRef,
    isOpen,
    highlightedIndex,
    panelId,
    labelId,
    isDisabled,
    isError,
    isSuccess,
    state: refState,
    isHighlighted,
    floatingStyles,
    showClearValueButton,
    focusInput,
    handleSelect,
    handleClearValueClick,
    handleTriggerKeydown,
    handleChevronClick,
    handleInputFocus,
    open,
    close,
  }
}
